/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import {
	type FormEventHandler,
	forwardRef,
	type Ref,
	type RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
} from 'react';

import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import CrossIcon from '@atlaskit/icon/core/cross';
import SearchIcon from '@atlaskit/icon/core/search';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner/spinner';
import Textfield from '@atlaskit/textfield/text-field';
import { token } from '@atlaskit/tokens';
import { addRecentSearch, RECENT_SEARCH_KEY } from '@atlassian/search-common/recent-searches';

import { ApplicationModes } from '../../../../common/constants/application-modes';
import { UserStatus } from '../../../../common/constants/types';
import { useAppContext } from '../../../../common/ui/app-context';
import { CopyLinkButton } from '../../../../common/ui/copy-link-button';
import { useWidthObserverRegistration } from '../../../../common/ui/width-observer';
import { mergeRefs } from '../../../../common/utils/merge-refs';
import { useFireCopyLinkButtonClicked } from '../../../../controllers/events/use-fire-copy-link-button-clicked';
import { usePlaceholderText } from '../../../../controllers/quick-find/input-placeholder';
import {
	useApplicationMode,
	useAvailableProducts,
	useFeatures,
	usePrimaryProduct,
	useUser,
} from '../../../../controllers/store/bootstrap';
import { useDropdown } from '../../../../controllers/store/dropdown';
import { useIsLoading, useSearchQuery } from '../../../../controllers/store/search';
import { messages } from './messages';

const textFieldBoxStyles = xcss({
	height: 'space.500',
	flexGrow: 1,
});
const cssOverrideWrapperStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles
	'> div': {
		height: '40px',
		borderWidth: token('border.width'),
		borderRadius: token('radius.medium'),
	},
});

const searchIconWrapperStyles = xcss({
	minWidth: 'size.200',
	minHeight: 'size.200',
	marginLeft: 'space.150',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
});

const rightIconStyles = xcss({
	marginRight: 'space.100',
});

export type SearchTextFieldProps = {
	onSubmit?: () => void;
	hasReceivedFocus?: () => void;
	keyboardRef?: RefObject<HTMLInputElement>;
	localQuery: string;
	setLocalQuery: (query: string) => void;
};

/**
 * Deprecated: This is used in non-nav4 and non-relay and basically legacy component
 */
export const SearchTextField: import('react').ForwardRefExoticComponent<
	SearchTextFieldProps & import('react').RefAttributes<HTMLDivElement>
> = forwardRef((props: SearchTextFieldProps, ref: Ref<HTMLDivElement>) => {
	const { localQuery, setLocalQuery, onSubmit } = props;

	const intl = useIntl();
	const { generateSearchUrl, queryParams, isAdminHubAIEnabled, isNav4Enabled } = useAppContext();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const [isLoading] = useIsLoading();
	const [searchQuery, { setInputQuery, triggerSearch }] = useSearchQuery();
	const [primaryProduct] = usePrimaryProduct();
	const [userWithStatus] = useUser();
	const [showDropdown, { setShowDropdown }] = useDropdown();
	const [features] = useFeatures();
	const availableProducts = useAvailableProducts();

	const fireCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchInput');

	const isUserAuthenticated = userWithStatus.status === UserStatus.Authenticated;
	const applicationMode = useApplicationMode();

	const { placeholder } = usePlaceholderText({
		applicationMode,
		isAdminHubAIEnabled,
		availableProducts,
		isNav4Enabled,
		quickFindOpen: true, // We only consider the open state for FPS
	});

	let hasReceivedFocus = useMemo(
		() => (props.hasReceivedFocus ? props.hasReceivedFocus : () => {}),
		[props.hasReceivedFocus],
	);

	// Sync local query with input query
	useEffect(() => {
		setInputQuery(localQuery);
	}, [setInputQuery, localQuery]);

	// Sync search query with local query
	// on search query change, determine intent / get smart answer route
	useEffect(() => {
		setLocalQuery(searchQuery);
	}, [searchQuery, setLocalQuery]);

	const handleOnSubmit = () => {
		// Store search in recent searches
		if (localQuery.trim() !== '' && userWithStatus && userWithStatus.id && primaryProduct) {
			addRecentSearch({
				value: localQuery,
				userId: userWithStatus.id,
				productId: primaryProduct,
				key: RECENT_SEARCH_KEY,
			});
		}

		if (onSubmit) {
			onSubmit();
		}

		if (features?.chromeExtension?.enabled) {
			features.chromeExtension.onInputSubmit({ query: localQuery });
		} else {
			triggerSearch();
		}
	};

	const onChange: FormEventHandler<HTMLInputElement> = useCallback(
		(e) => {
			setLocalQuery(e.currentTarget.value);

			hasReceivedFocus();

			if (e.currentTarget.value === '' && !isUserAuthenticated) {
				setShowDropdown(false);
			} else if (e.currentTarget.value !== '') {
				setShowDropdown(true);
			}
		},
		[hasReceivedFocus, isUserAuthenticated, setLocalQuery, setShowDropdown],
	);

	const onClick = () => {
		hasReceivedFocus();
		if (localQuery === '' && !isUserAuthenticated) {
			setShowDropdown(false);
		}
	};

	const onKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Enter') {
			handleOnSubmit();
			event.currentTarget.blur();
		}
	};

	const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'ArrowDown') {
			hasReceivedFocus();
		}
		if (showDropdown && event.key === 'Tab') {
			if (onSubmit) {
				onSubmit();
			}
		}
	};

	const searchBoxRef = useRef(null);
	useWidthObserverRegistration('search-box', searchBoxRef);

	return (
		<Box xcss={textFieldBoxStyles} ref={mergeRefs(ref, searchBoxRef)}>
			<div css={cssOverrideWrapperStyles}>
				<Textfield
					ref={props.keyboardRef}
					id="text-query-input"
					testId="text-query-input"
					value={localQuery}
					onKeyPress={(event: React.KeyboardEvent<HTMLInputElement>) => onKeyPress(event)}
					onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => onKeyDown(event)}
					onChange={onChange}
					onClick={onClick}
					autoComplete="off"
					placeholder={placeholder}
					elemBeforeInput={
						<Box xcss={searchIconWrapperStyles}>
							{isLoading ? (
								<Spinner testId="search-page-input-spinner" size="small" />
							) : (
								<SearchIcon
									color="currentColor"
									label={intl.formatMessage(messages.searchIconLabel)}
								/>
							)}
						</Box>
					}
					elemAfterInput={
						localQuery && (
							<Inline space="space.050" xcss={rightIconStyles}>
								{applicationMode === ApplicationModes.Rovo && (
									<CopyLinkButton
										fullResultUrl={() => generateSearchUrl({ ...queryParams, text: localQuery })}
										showSuccess
										isButton
										onClick={fireCopyLinkButtonClicked}
										testId="search-input-copy-link-button"
									/>
								)}
								<IconButton
									icon={(iconProps) => (
										<CrossIcon {...iconProps} color="currentColor" size="small" />
									)}
									spacing="compact"
									onClick={() => setLocalQuery('')}
									label={intl.formatMessage(messages.clearIconLabel)}
									appearance="subtle"
								/>
							</Inline>
						)
					}
					autoFocus
				/>
			</div>
		</Box>
	);
});
